import { useState } from 'react';
import { Menu, X, Bell, User, LogOut, Settings } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNotification } from '@/hooks/useNotification';
import rgfLogo from '@/assets/rgf-logo.png';

interface HeaderProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

export const Header: React.FC<HeaderProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const { showSuccess } = useNotification();

  const handleLogout = async () => {
    try {
      await logout();
      showSuccess('Logged out', 'You have been successfully logged out');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-rgf-gray-200 sticky top-0 z-40">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Left section */}
          <div className="flex items-center">
            {/* Sidebar toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg text-rgf-gray-600 hover:text-rgf-gray-900 hover:bg-rgf-gray-100 focus:outline-none focus:ring-2 focus:ring-rgf-primary-500"
            >
              {sidebarOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>

            {/* Logo and title */}
            <div className="ml-4 flex items-center">
              <img className="h-8 w-auto" src={rgfLogo} alt="Roundglass Foundation" />
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-rgf-gray-900">
                  Back Office
                </h1>
              </div>
            </div>
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <button className="p-2 rounded-lg text-rgf-gray-600 hover:text-rgf-gray-900 hover:bg-rgf-gray-100 focus:outline-none focus:ring-2 focus:ring-rgf-primary-500 relative">
              <Bell className="h-6 w-6" />
              {/* Notification badge */}
              <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-xs font-medium text-white">3</span>
              </span>
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center space-x-3 p-2 rounded-lg text-rgf-gray-600 hover:text-rgf-gray-900 hover:bg-rgf-gray-100 focus:outline-none focus:ring-2 focus:ring-rgf-primary-500"
              >
                <div className="h-8 w-8 bg-rgf-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-rgf-primary-700">
                    {user?.email?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-rgf-gray-900">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-rgf-gray-500">
                    {user?.roles?.[0] || 'User'}
                  </p>
                </div>
              </button>

              {/* Dropdown menu */}
              {dropdownOpen && (
                <>
                  {/* Overlay */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setDropdownOpen(false)}
                  />
                  
                  {/* Menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-rgf-gray-200 py-1 z-20">
                    <div className="px-4 py-2 border-b border-rgf-gray-100">
                      <p className="text-sm font-medium text-rgf-gray-900">
                        {user?.email}
                      </p>
                      <p className="text-xs text-rgf-gray-500">
                        {user?.roles?.join(', ') || 'No roles assigned'}
                      </p>
                    </div>
                    
                    <a
                      href="#"
                      className="flex items-center px-4 py-2 text-sm text-rgf-gray-700 hover:bg-rgf-gray-100"
                      onClick={(e) => {
                        e.preventDefault();
                        setDropdownOpen(false);
                      }}
                    >
                      <User className="mr-3 h-4 w-4" />
                      Profile
                    </a>
                    
                    <a
                      href="#"
                      className="flex items-center px-4 py-2 text-sm text-rgf-gray-700 hover:bg-rgf-gray-100"
                      onClick={(e) => {
                        e.preventDefault();
                        setDropdownOpen(false);
                      }}
                    >
                      <Settings className="mr-3 h-4 w-4" />
                      Settings
                    </a>
                    
                    <div className="border-t border-rgf-gray-100" />
                    
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};